// proposal-applier.ts
//
// Áp một `proposed_change` lên tập ticket như MỘT giao dịch trong bộ nhớ:
// hoặc trả về bộ (tickets, statusMap) mới đã đầy đủ, hoặc trả lỗi và không
// thay đổi gì. Thuần — caller (supervisor) mới là nơi ghi ra journal/git.
//
// Hai bất biến:
//   1. Optimistic concurrency: mọi thao tác lên ticket có sẵn phải khai
//      `expected_revision` khớp revision hiện tại. Lệch ⇒ `stale_revision`.
//      Không có nó, hai đề xuất dựng trên cùng bản cũ sẽ lặng lẽ đè nhau.
//   2. Huỷ một ticket phải cập nhật bên phụ thuộc TRONG CÙNG kết quả. Tách
//      làm hai lần ghi sẽ có một khoảnh khắc mà scheduler nhìn thấy dependent
//      "todo" với dependency đã biến mất.
export { BOOTSTRAP_KINDS, type ProposedChange } from "./proposal-change-types.js";

import { isControllerReservedTicketId } from "./controller-reserved-ticket-ids.js";
import { ticketAcceptanceRejection } from "./feature-ticket-acceptance-required.js";
import type { ProposedChange } from "./proposal-change-types.js";
import { checkExistingTicketChange } from "./proposal-existing-ticket-guards.js";
import { checkProposedScope } from "./proposal-scope-guard.js";
import { summarizeSchemaIssues } from "./schema-issue-summary.js";
import { type Ticket, type TicketAcceptance, TicketSchema } from "./schemas/ticket-schema.js";
import { ticketUnrunnableInV1Reason } from "./ticket-v1-runnability.js";

export type ApplyProposalResult =
  | { ok: true; tickets: Ticket[]; statusMap: Record<string, string> }
  | { ok: false; reason: string };

function reject(reason: string): ApplyProposalResult {
  return { ok: false, reason };
}

/** Ticket phụ thuộc trực tiếp vào `ticketId`. */
function dependentsOf(tickets: Ticket[], ticketId: string): Ticket[] {
  return tickets.filter((t) => t.depends_on.includes(ticketId));
}

/**
 * Trạng thái đóng bằng một quyết định đã chốt, không phải bằng kết quả chạy.
 * Guard acceptance dưới đây không được lật chúng về `todo`.
 */
const RESOLVED_BY_HUMAN_STATUSES = new Set(["cancelled", "superseded"]);

/**
 * M1: `acceptance[]` của bản cũ và bản amend có khác nhau về NỘI DUNG không.
 *
 * So từng trường (`text`/`done`/`evidence`) thay vì `JSON.stringify` — hai
 * object cùng nội dung khác thứ tự key sẽ ra chuỗi khác nhau, và phạt một
 * amend không đổi gì là đúng loại "guard chặn nhầm rồi bị người ta xoá".
 * `undefined` ở `before` (ticket viết trước C1) mà `after` có dòng ⇒ khác.
 */
function acceptanceChanged(
  before: readonly TicketAcceptance[] | undefined,
  after: readonly TicketAcceptance[],
): boolean {
  if (before === undefined) return after.length > 0;
  if (before.length !== after.length) return true;
  return after.some((row, index) => {
    const old = before[index];
    if (old === undefined) return true;
    return old.text !== row.text || old.done !== row.done || old.evidence !== row.evidence;
  });
}

function applyCreate(
  tickets: Ticket[],
  statusMap: Record<string, string>,
  change: ProposedChange,
): ApplyProposalResult {
  if (change.new_ticket === undefined) return reject("create proposal thiếu new_ticket");
  // Review P4 L1: a redirect payload reaches here without the done-path's wire
  // parse — the new ticket must be a valid Ticket before any guard reads it.
  const parsed = TicketSchema.safeParse(change.new_ticket);
  if (!parsed.success)
    return reject(`create làm ticket sai ticket.schema: ${summarizeSchemaIssues(parsed.error)}`);
  const fresh = parsed.data;
  if (isControllerReservedTicketId(fresh.id)) {
    return reject(
      `ticket id ${fresh.id} dành cho controller (phiên review) — worker không được create`,
    );
  }
  if (tickets.some((t) => t.id === fresh.id)) {
    return reject(`ticket ${fresh.id} đã tồn tại — create không được ghi đè`);
  }
  const scopeReason = checkProposedScope(fresh.allowed_paths);
  if (scopeReason) return reject(scopeReason);
  // H14: a deploy/revert ticket (or one whose adapter disagrees with its
  // kind) could never be dispatched in v1 — refuse it here, not three
  // failed attempts later.
  const unrunnable = ticketUnrunnableInV1Reason(fresh);
  if (unrunnable !== undefined) return reject(unrunnable);
  const acceptanceReason = ticketAcceptanceRejection(fresh); // 06e R4 (F-27)
  if (acceptanceReason !== undefined) return reject(acceptanceReason);
  return {
    ok: true,
    tickets: [...tickets, fresh],
    statusMap: { ...statusMap, [fresh.id]: "todo" },
  };
}

export function applyProposal(
  tickets: Ticket[],
  statusMap: Record<string, string>,
  change: ProposedChange,
): ApplyProposalResult {
  if (change.op === "create") return applyCreate(tickets, statusMap, change);

  const ticketId = change.ticket_id;
  if (ticketId === undefined) return reject(`${change.op} proposal thiếu ticket_id`);

  const target = tickets.find((t) => t.id === ticketId);
  if (target === undefined) return reject(`ticket ${ticketId} không tồn tại trong tập hiện hành`);

  if (change.expected_revision !== target.revision) {
    return reject("stale_revision");
  }

  // Sửa nội dung ⇒ revision tiến một bậc, để đề xuất kế tiếp dựng trên bản cũ
  // bị bắt là stale. CXR-05: the patch is untrusted worker output — the merged
  // ticket must be a valid Ticket (strict schema) before anything is written.
  // Review P4 M1: parsed BEFORE the guards, which iterate patch fields
  // (`allowed_paths: [1]` used to throw a TypeError mid-tick).
  let amended: Ticket | undefined;
  if (change.op === "amend") {
    const parsedAmend = TicketSchema.safeParse({
      ...target,
      ...change.patch,
      id: target.id,
      revision: target.revision + 1,
    });
    if (!parsedAmend.success) {
      return reject(
        `amend làm ticket sai ticket.schema: ${summarizeSchemaIssues(parsedAmend.error)}`,
      );
    }
    amended = parsedAmend.data;
  }

  const guardReason = checkExistingTicketChange(target, change);
  if (guardReason !== undefined) return reject(guardReason);

  const nextStatus = { ...statusMap };

  if (amended !== undefined) {
    // H14: judge the ticket as it will BE — an amend that only swaps
    // `command_adapter` to deploy-v1 never touches `kind`, which is all the
    // Bug L guard above looks at.
    const unrunnable = ticketUnrunnableInV1Reason(amended);
    if (unrunnable !== undefined) return reject(unrunnable);
    const acceptanceReason = ticketAcceptanceRejection(amended, change.patch ?? {});
    if (acceptanceReason !== undefined) return reject(acceptanceReason);
    // M1: `acceptance[]` là thứ C1 (`checkAcceptanceDoneHasEvidence`) đo, và
    // verifier chỉ đo nó lúc ticket ĐANG chạy. Một amend đổi acceptance của
    // ticket đã `done` vì thế đi vòng qua C1 hoàn toàn: dòng mới tick
    // `done: true` với evidence do worker tự khai không bao giờ gặp verifier.
    // Cùng khuôn `cancel` ép dependent thành `blocked` ngay trong giao dịch
    // này: đổi điều kiện nghiệm thu ⇒ kết luận cũ hết hiệu lực, ticket về
    // `todo` để chạy lại và để C1 đo bản mới.
    //
    // Chỉ phạt khi acceptance THỰC SỰ đổi — amend `title`/`outcome`/
    // `allowed_paths` không đụng cái đang được đo, ép chúng chạy lại là chặn
    // nhầm đường chính của mission.
    //
    // Không áp cho ticket `cancelled`/`superseded`: đó là quyết định ĐÃ CHỐT
    // của người (hoặc của một op cancel/supersede trước đó), và ép `todo` lên
    // chúng là hồi sinh việc đã bỏ — scheduler thấy `todo` với depends_on đã
    // done là dispatch lại, đốt attempt budget cho thứ không ai muốn chạy.
    if (
      change.patch?.acceptance !== undefined &&
      !RESOLVED_BY_HUMAN_STATUSES.has(statusMap[ticketId] ?? "todo") &&
      acceptanceChanged(target.acceptance, change.patch.acceptance)
    ) {
      nextStatus[ticketId] = "todo";
    }
    return {
      ok: true,
      tickets: tickets.map((t) => (t.id === ticketId ? amended : t)),
      statusMap: nextStatus,
    };
  }

  if (change.op === "cancel") {
    nextStatus[ticketId] = "cancelled";
    // Dependency đã huỷ KHÔNG thoả điều kiện ready (scheduler chỉ chấp nhận
    // "done"), nên bên phụ thuộc phải chuyển "blocked" ngay trong giao dịch
    // này — người vận hành cần thấy chúng đang chờ quyết định, chứ không phải
    // treo vô hình ở "todo".
    for (const dependent of dependentsOf(tickets, ticketId)) {
      nextStatus[dependent.id] = "blocked";
    }
    return { ok: true, tickets: [...tickets], statusMap: nextStatus };
  }

  // supersede: bản gốc không còn được tính là "done" dù trước đó đã xong —
  // công việc nó đại diện đã được ticket khác thay thế.
  nextStatus[ticketId] = "superseded";
  return { ok: true, tickets: [...tickets], statusMap: nextStatus };
}
